import { useQuery } from '@tanstack/react-query';
import { TZDate } from '@date-fns/tz';
import { useParams } from 'react-router-dom';
import { useApi, useAuth } from '../../hooks';
import { CareModel } from '../../models/user';
import { formatDateToDayOnly } from '../../components/AppointmentsPage/utils';
import {
  AvailabilityEntry,
  MeetingInfo,
  MeetingType as MeetingTypeId,
} from '../../models/meeting';
import qs from 'qs';

type CoachAvailabilityResponse = {
  meeting_type: MeetingInfo;
  availability: AvailabilityEntry[];
};

type Props = {
  care_model?: CareModel;
  member_uuid?: string;
  endDate?: TZDate;
  meetingType?: MeetingTypeId | null;
  startDate?: TZDate;
  timezone?: string;
};

export const useCoachAvailability = ({
  care_model,
  member_uuid,
  endDate,
  meetingType: appt_type,
  startDate,
  timezone,
}: Props) => {
  const { patient: userId } = useParams();
  const { api } = useApi();
  const { userToken } = useAuth();

  const fetchAvailability = async (): Promise<CoachAvailabilityResponse> => {
    if (!startDate || !endDate) {
      return { availability: [] } as unknown as CoachAvailabilityResponse;
    }

    const start_date = formatDateToDayOnly(startDate);
    const end_date = formatDateToDayOnly(endDate);

    const queryStr = qs.stringify({
      appt_type,
      end_date,
      legacy_provider_appt_typing: false,
      start_date,
      timezone,
    });

    return api({
      route: `/private/care-team-member/${member_uuid}/availability${
        queryStr ? `?${queryStr}` : ''
      }`,
      token: userToken,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Coach-Override': userId ?? '',
      },
    });
  };

  const { data, isInitialLoading, isLoading, isError } = useQuery<
    CoachAvailabilityResponse,
    Error
  >({
    queryKey: [
      'public-coach-availability',
      member_uuid,
      userId,
      endDate,
      appt_type,
      startDate,
      timezone,
    ],
    queryFn: fetchAvailability,
    enabled:
      !!care_model &&
      !!member_uuid &&
      !!userId &&
      !!endDate &&
      !!appt_type &&
      !!startDate &&
      !!timezone,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isInitialLoading || isLoading,
    isError,
    data,
  };
};
